import {
  Box,
  Card,
  Container,
  createStyles,
  Group,
  Menu,
  rem,
  Space,
  Tabs,
  TabsValue,
  Text,
  Title,
  UnstyledButton
} from "@mantine/core";
import {IconCheckbox, IconChevronDown, IconMap, IconShieldFilled} from "@tabler/icons-react";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback, useMemo} from "react";
import {TicketDetails} from "./TicketDetails";
import {useTranslation} from "react-i18next";
import {TicketActionMenuDropdown} from "../../menu/TicketActionMenu/TicketActionMenu";
import {useGetComplianceProvidersQuery, TransportAssocEntity} from "@ots-frontend/ots-data";
import {TabItem} from "../../tabs/Tabs/Tabs";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {TicketScada} from "./TicketScada";
import {useTicketAction} from "../../menu/TicketActionMenu/useTicketAction";
import {DatabaseCodeBadge} from "../../badge/DatabaseCodeBadge/DatabaseCodeBadge";
import {Tooltip} from "../../tooltip/Tooltip/Tooltip";
import {TicketValidationText} from "../../text/TicketValidationText/TicketValidationText";
import {TicketCompliance} from "./TicketCompliance";

const useStyles = createStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type TicketProfileProps = PropsWithChildren<{
  ticket: TransportAssocEntity
}>

export const TicketProfile = (props: TicketProfileProps) => {
  const {ticket} = props;

  const {classes} = useStyles();

  const complianceProviders = useGetComplianceProvidersQuery({kind: 'ticket', id: ticket.id, tenantCode: ticket.database});

  const {t} = useTranslation()
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();

  const {onTicketActionClick} = useTicketAction()

  const onTabChange = useCallback((value: TabsValue) => {
    if (value !== 'map' && value !== 'dispatch') {
      navigate(pathname + search, {replace: true, state: {...state, tabValue: value}})
    } else if (value === 'map') {
      navigate(`map?id=${ticket.id}&database=${ticket.database}`)
    } else if (value === 'dispatch') {
      navigate(`/dispatch/profile?id=${ticket.dispatchId}`)
    }
  }, [navigate, pathname, search, state, ticket])

  const showCompliance = complianceProviders.isFetching || !!complianceProviders.error ||
    (complianceProviders.currentData?.length ?? 0) > 0;

  const tabs = useMemo(() => {
    const tabs: TabItem[] = [
      {
        key: "details",
        value: "Details"
      },
      {
        key: "scada",
        value: "Scada"
      },
      {
        key: "map",
        value: "Map",
        icon: <IconMap size={14}/>
      }
    ]

    if (ticket.dispatchId) {
      tabs.push({
        key: "dispatch",
        value: "Dispatch",
        icon: <IconCheckbox size={14}/>,
        permissions: ["dispatch_view"],
      })
    }

    if (showCompliance) {
      tabs.push({
        key: "compliance",
        value: "Compliance",
        icon: <IconShieldFilled size={14}/>
      })
    }

    return tabs
  }, [ticket, showCompliance])

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key} icon={tab.icon}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>Ticket: {ticket.resourceIdentifiers.join()}</Title>
          <Menu
            width={250}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <TicketActionMenuDropdown
                onActionClick={(action) => onTicketActionClick(ticket, action)}
                ticket={ticket}/>
            </Menu.Dropdown>
          </Menu>
        </Group>
        <Group p="md">
          {ticket.validationStatus &&
            <Tooltip
              withinPortal={false}
              multiline={true}
              label={
                <TicketValidationText
                  data={ticket}
                  separator={"\n"}
                  style={{whiteSpace: "pre-wrap"}}/>
              }
            >
              <Box>
                <DatabaseCodeBadge value={ticket.validationStatus} />
              </Box>
            </Tooltip>
          }
        </Group>
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={onTabChange}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <TicketDetails ticket={ticket}/>
            </Tabs.Panel>
            <Tabs.Panel value="scada" w="100%">
              <TicketScada ticket={ticket}/>
            </Tabs.Panel>
            <Tabs.Panel value="map">
              <></>
            </Tabs.Panel>
            <Tabs.Panel value="dispatch">
              <></>
            </Tabs.Panel>
            {showCompliance &&
              <Tabs.Panel value="compliance">
                <TicketCompliance ticket={ticket} />
              </Tabs.Panel>
            }
          </Tabs>
        </Container>
      </Card.Section>
    </Card>
  );
};
